import './LitPopup.scss';
import { LitElement } from 'lit';
import LitPopup from 'lit-popup';
import { getCSSCustomProp } from '../../utils/css';

export interface LitPopupElement {
    instance: LitPopup;
    _wasBodyLocked: boolean;
    _leaveTimeout: NodeJS.Timeout;
    _removeFocusTrap?: () => void;
}

const NO_SCROLL_CLASS = 'no-scroll';

export class LitPopupElement extends LitElement {
    constructor() {
        super();
        this._wasBodyLocked = false;
    }

    static get properties() {
        return {
            name: {
                type: Boolean,
            },
            opened: {
                type: String,
            },
        };
    }

    createRenderRoot() {
        /**
         * Render template without shadow DOM. Note that shadow DOM features like
         * encapsulated CSS and slots are unavailable.
         */
        return this;
    }

    connectedCallback() {
        super.connectedCallback();
        const name = this.getAttribute('data-lit-popup');
        const leaveDurationInSeconds = getCSSCustomProp(this, '--leave-duration', 'number') as number;

        if (!name) {
            throw new Error('[lit-popup] Name should be provided.');
        }

        this.instance = new LitPopup(name, {
            onOpen: () => {
                clearTimeout(this._leaveTimeout);
                this._lockBodyScroll();
                this._setFocus();
                this._trapFocus();
            },
            onOpenComplete: () => {
                const focusableOnOpenElement = this.renderRoot.querySelector<HTMLElement>('[data-focus-on-popup-open]');
                setTimeout(() => focusableOnOpenElement?.focus({ preventScroll: true }), 50);
            },
            onClose: () => {
                this._leaveTimeout = setTimeout(() => {
                    this._unlockBodyScroll();
                    this._untrapFocus();
                }, leaveDurationInSeconds * 1000);
            },
        });
    }

    attributeChangedCallback(name: string, oldVal: string | null, newVal: string | null) {
        super.attributeChangedCallback(name, oldVal, newVal);

        if (name === 'opened') {
            if (typeof newVal === 'string') {
                this.instance.open();
            } else {
                this.instance.close();
            }
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        clearTimeout(this._leaveTimeout);

        if (this.instance) {
            this.instance.destroy();
        }
        this._untrapFocus();
    }

    open() {
        this.instance?.open();
    }

    close() {
        this.instance?.close();
    }

    protected _lockBodyScroll() {
        this._wasBodyLocked = document.documentElement.classList.contains(NO_SCROLL_CLASS);

        if (!this._wasBodyLocked && !this.classList.contains('js-no-block-scroll')) {
            document.documentElement.classList.add(NO_SCROLL_CLASS);
        }
    }

    protected _unlockBodyScroll() {
        if (!this._wasBodyLocked) {
            document.documentElement.classList.remove(NO_SCROLL_CLASS);
        }
    }

    protected _setFocus() {
        if (!this) {
            console.warn('[LitPopup] Modal container with class ".lit-popup" not found.');
            return;
        }

        const focusableSelectors = [
            'button:not([disabled])',
            '[href]:not([aria-disabled="true"])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"]):not([disabled])',
        ].join(', ');

        const focusableElements = this.querySelectorAll<HTMLElement>(focusableSelectors);
        const firstFocusable = focusableElements.length > 0 ? focusableElements[0] : null;

        setTimeout(() => {
            if (firstFocusable) {
                firstFocusable.focus({ preventScroll: true });
            } else {
                if (this.tabIndex < 0) {
                    this.tabIndex = 0;
                }
                this.focus({ preventScroll: true });
            }
        }, 50);
    }

    protected _trapFocus() {
        const focusableSelectors = [
            'button:not([disabled])',
            '[href]:not([aria-disabled="true"])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"]):not([disabled])',
        ].join(', ');

        const focusableElements = this.querySelectorAll<HTMLElement>(focusableSelectors);
        if (focusableElements.length === 0) return;

        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        function handleTab(e: KeyboardEvent) {
            if (e.key !== 'Tab') return;
            if (e.shiftKey) {
                if (document.activeElement === firstFocusable) {
                    lastFocusable.focus();
                    e.preventDefault();
                }
            } else {
                if (document.activeElement === lastFocusable) {
                    firstFocusable.focus();
                    e.preventDefault();
                }
            }
        }

        this.addEventListener('keydown', handleTab);
        this._removeFocusTrap = () => this.removeEventListener('keydown', handleTab);
    }

    protected _untrapFocus() {
        this._removeFocusTrap?.();
        this._removeFocusTrap = undefined;
    }
}

customElements.define('app-lit-popup', LitPopupElement);

declare global {
    interface HTMLElementTagNameMap {
        'app-lit-popup': LitPopupElement;
    }
}
